import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { cryptoAPI } from '../services/api';

interface CoinData {
  id: number;
  name: string;
  symbol: string;
  cmc_rank: number;
  image?: string;
  quote: {
    USD: {
      price: number;
      percent_change_24h: number;
      market_cap: number;
      volume_24h: number;
      high_24h?: number;
      low_24h?: number;
    };
  };
}

interface CoinDataContextType {
  coins: CoinData[];
  coinImageMap: Map<string, string>;
  coinDataMap: Map<string, CoinData>;
  loading: boolean;
  error: string | null;
  totalAvailable: number; // Total coins available (14,000)
  loadedCount: number; // How many coins are currently loaded
  refreshData: () => Promise<void>;
  loadMoreCoins: (neededCount: number) => Promise<void>;
  getCoinIcon: (symbol: string) => string;
  getCoinData: (symbol: string) => CoinData | null;
}

const CoinDataContext = createContext<CoinDataContextType | undefined>(undefined);

// Cache duration: 5 minutes (300000 ms)
const CACHE_DURATION = 5 * 60 * 1000;
const CACHE_KEY = 'coin_data_cache';
const CACHE_TIMESTAMP_KEY = 'coin_data_cache_timestamp';

interface CachedData {
  coins: CoinData[];
  timestamp: number;
}

export const CoinDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const [coins, setCoins] = useState<CoinData[]>([]);
  const [coinImageMap, setCoinImageMap] = useState<Map<string, string>>(new Map());
  const [coinDataMap, setCoinDataMap] = useState<Map<string, CoinData>>(new Map());
  const [loading, setLoading] = useState<boolean>(false); // Start as false, only load when needed
  const [error, setError] = useState<string | null>(null);
  const [totalAvailable] = useState<number>(14000); // Total coins available
  const isFetchingRef = useRef<boolean>(false);
  const lastFetchTimeRef = useRef<number>(0);
  const isFetchingMoreRef = useRef<boolean>(false);

  // Load from localStorage cache
  const loadFromCache = useCallback((): CachedData | null => {
    try {
      const cachedData = localStorage.getItem(CACHE_KEY);
      const cachedTimestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);
      
      if (cachedData && cachedTimestamp) {
        const timestamp = parseInt(cachedTimestamp, 10);
        const now = Date.now();
        
        // Check if cache is still valid (within cache duration)
        if (now - timestamp < CACHE_DURATION) {
          return {
            coins: JSON.parse(cachedData),
            timestamp,
          };
        }
      }
    } catch (error) {
      console.error('Error loading cache:', error);
    }
    return null;
  }, []);

  // Save to localStorage cache
  const saveToCache = useCallback((data: CoinData[]) => {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(data));
      localStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
    } catch (error) {
      console.error('Error saving cache:', error);
    }
  }, []);

  // Build maps from coins data
  const buildMaps = useCallback((coinsData: CoinData[]) => {
    const imageMap = new Map<string, string>();
    const dataMap = new Map<string, CoinData>();
    
    coinsData.forEach((coin) => {
      if (coin.symbol) {
        const symbolUpper = coin.symbol.toUpperCase();
        if (coin.image) {
          imageMap.set(symbolUpper, coin.image);
        }
        dataMap.set(symbolUpper, coin);
      }
    });
    
    setCoinImageMap(imageMap);
    setCoinDataMap(dataMap);
  }, []);

  // Initial fetch - only load first 250 coins
  const fetchCoinData = useCallback(async (forceRefresh: boolean = false) => {
    // Prevent multiple simultaneous fetches
    if (isFetchingRef.current && !forceRefresh) {
      return;
    }

    // Check if we should use cache
    if (!forceRefresh) {
      const cached = loadFromCache();
      if (cached) {
        setCoins(cached.coins);
        buildMaps(cached.coins);
        setLoading(false);
        setError(null);
        lastFetchTimeRef.current = cached.timestamp;
        
        // Fetch fresh data in background (don't wait for it)
        fetchCoinData(true).catch(() => {
          // Silent fail for background refresh
        });
        return;
      }

      // Check if we fetched recently (within last 30 seconds)
      const timeSinceLastFetch = Date.now() - lastFetchTimeRef.current;
      if (timeSinceLastFetch < 30000 && coins.length > 0) {
        return;
      }
    }

    isFetchingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      // Initial load: only fetch 250 coins (1 page)
      const response = await cryptoAPI.getListings({ limit: '250', start: '1' });
      
      if (response.data?.data && Array.isArray(response.data.data)) {
        const coinsData = response.data.data as CoinData[];
        setCoins(coinsData);
        buildMaps(coinsData);
        saveToCache(coinsData);
        lastFetchTimeRef.current = Date.now();
        setError(null);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err: any) {
      console.error('Error fetching coin data:', err);
      
      // Try to use cache even if expired
      const cached = loadFromCache();
      if (cached) {
        setCoins(cached.coins);
        buildMaps(cached.coins);
        setError('Using cached data. Some information may be outdated.');
      } else {
        setError('Failed to load coin data. Please try again.');
      }
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [loadFromCache, saveToCache, buildMaps, coins.length]);

  // Load more coins on demand
  const loadMoreCoins = useCallback(async (neededCount: number) => {
    // Don't load if already have enough or already fetching
    if (coins.length >= neededCount || isFetchingMoreRef.current) {
      return;
    }

    // Calculate how many more we need
    const currentCount = coins.length;
    const toLoad = Math.min(neededCount - currentCount, 250); // Load in batches of 250
    const startIndex = currentCount + 1; // Start from next coin

    if (toLoad <= 0) return;

    isFetchingMoreRef.current = true;
    setError(null);

    try {
      // Fetch next batch
      const response = await cryptoAPI.getListings({ 
        limit: String(toLoad), 
        start: String(startIndex) 
      });
      
      if (response.data?.data && Array.isArray(response.data.data)) {
        const newCoins = response.data.data as CoinData[];
        // Merge with existing coins
        const updatedCoins = [...coins, ...newCoins];
        setCoins(updatedCoins);
        buildMaps(updatedCoins);
        saveToCache(updatedCoins);
        setError(null);
      }
    } catch (err: any) {
      console.error('Error loading more coins:', err);
      setError('Failed to load more coins. Please try again.');
    } finally {
      isFetchingMoreRef.current = false;
    }
  }, [coins, buildMaps, saveToCache]);

  // Refresh data manually
  const refreshData = useCallback(async () => {
    await fetchCoinData(true);
  }, [fetchCoinData]);

  // Get coin icon URL
  const getCoinIcon = useCallback((symbol: string): string => {
    if (!symbol) return 'https://via.placeholder.com/32?text=?';
    
    const symbolUpper = symbol.toUpperCase();
    const imageUrl = coinImageMap.get(symbolUpper);
    
    if (imageUrl) {
      return imageUrl;
    }
    
    // Fallback to symbol-based URL
    const { getCoinIconUrlBySymbol } = require('../shared/utils/coinUtils');
    return getCoinIconUrlBySymbol(symbol);
  }, [coinImageMap]);

  // Get coin data by symbol
  const getCoinData = useCallback((symbol: string): CoinData | null => {
    if (!symbol) return null;
    return coinDataMap.get(symbol.toUpperCase()) || null;
  }, [coinDataMap]);

  // Only fetch data if we're on a dashboard page (lazy loading)
  useEffect(() => {
    const isDashboardRoute = location.pathname.startsWith('/dashboard');
    if (isDashboardRoute && coins.length === 0 && !isFetchingRef.current && !loading) {
      fetchCoinData();
    }
  }, [location.pathname, fetchCoinData, coins.length, loading]);

  // Auto-refresh every 5 minutes (only if data is already loaded and on dashboard)
  useEffect(() => {
    if (coins.length === 0) return; // Don't set interval if no data loaded
    
    const isDashboardRoute = location.pathname.startsWith('/dashboard');
    if (!isDashboardRoute) return; // Don't refresh if not on dashboard
    
    const interval = setInterval(() => {
      fetchCoinData(true);
    }, CACHE_DURATION);

    return () => clearInterval(interval);
  }, [location.pathname, fetchCoinData, coins.length]);

  const value: CoinDataContextType = {
    coins,
    coinImageMap,
    coinDataMap,
    loading,
    error,
    totalAvailable,
    loadedCount: coins.length,
    refreshData,
    loadMoreCoins,
    getCoinIcon,
    getCoinData,
  };

  return (
    <CoinDataContext.Provider value={value}>
      {children}
    </CoinDataContext.Provider>
  );
};

export const useCoinData = (): CoinDataContextType => {
  const context = useContext(CoinDataContext);
  if (context === undefined) {
    throw new Error('useCoinData must be used within a CoinDataProvider');
  }
  return context;
};
